import { ExternalLink, FileText, Info } from "lucide-react";

const n = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString("en-US") : v);
const METRICS = [["views", "Views"], ["reach", "Reach"], ["likes", "Likes"], ["saves", "Saves"], ["shares", "Shares"], ["comments", "Comments"]];

export function SourceLine({ source, note }) {
  return (
    <div data-testid="wrap-source" className="flex flex-col gap-1 text-xs text-neutral-500">
      {source && <p className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {source}</p>}
      {note && <p className="flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> {note}</p>}
    </div>
  );
}

function Headline({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-4" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="num-display mt-1 text-2xl font-extrabold">{n(value)}</p>
    </div>
  );
}

function TopPosts({ posts, testId }) {
  if (!posts.length) return null;
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold text-neutral-600">Top performing posts named in the report (not a full post list)</p>
      <table className="mt-2 w-full text-sm" data-testid={testId}>
        <thead><tr className="text-left text-xs text-neutral-400"><th className="py-1 font-medium">Handle</th><th className="py-1 text-right font-medium">Views</th><th className="py-1 text-right font-medium">Likes</th></tr></thead>
        <tbody>
          {posts.map((p, i) => (
            <tr key={i} className="border-t border-black/[0.05]"><td className="py-1.5">@{p.handle}</td><td className="py-1.5 text-right tabular-nums">{n(p.views)}</td><td className="py-1.5 text-right tabular-nums">{n(p.likes)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Block({ b, slug }) {
  const id = `${slug}-${b.name.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="pb-card p-5" data-testid={`wrap-block-${id}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-lg font-bold">{b.name}</h3>
        <span className="text-xs text-neutral-500">{b.posts_label}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
        {METRICS.map(([k, label]) => (
          <div key={k}><p className="text-[11px] text-neutral-500">{label}</p><p className="font-semibold tabular-nums" data-testid={`wrap-${id}-${k}`}>{n(b.metrics[k])}</p></div>
        ))}
      </div>
      {b.note && <p className="mt-3 text-xs text-neutral-500">{b.note}</p>}
      <TopPosts posts={b.top_posts} testId={`wrap-${id}-top-posts`} />
    </div>
  );
}

export function AudioWrap({ audio, source, note }) {
  return (
    <div data-testid={`wrap-audio-${audio.slug}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]" data-testid="wrap-kicker">{audio.kicker || "Colture x @hotel Wrap Report"}</p>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">{audio.title} <span className="text-neutral-400">— {audio.artist}</span></h1>
      <p className="mt-1 text-sm text-neutral-500" data-testid="wrap-period">{audio.period}</p>
      <div className="mt-3"><SourceLine source={source} note={note} /></div>
      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Headline label="Views" value={audio.headline.views} testId="wrap-headline-views" />
        <Headline label="Likes" value={audio.headline.likes} testId="wrap-headline-likes" />
        <Headline label="Saves+Shares" value={audio.headline.saves_shares} testId="wrap-headline-saves-shares" />
      </div>
      <p className="mt-3 text-sm text-neutral-600" data-testid="wrap-content">Content overview: {audio.content.join(" · ")}</p>
      <div className="mt-6 space-y-4">{audio.blocks.map((b) => <Block key={b.name} b={b} slug={audio.slug} />)}</div>
    </div>
  );
}

export function RecapCard({ recap, source, note }) {
  return (
    <div className="pb-card p-6" data-testid="wrap-recap">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Colture x @hotel · {recap.title}</p>
      <p className="mt-1 text-sm text-neutral-600">{recap.audios} audios · {recap.artists}</p>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Headline label="Views" value={recap.views} testId="recap-views" />
        <Headline label="Likes" value={recap.likes} testId="recap-likes" />
        <Headline label="Saves+Shares" value={recap.saves_shares} testId="recap-saves-shares" />
        <Headline label="Engagement rate" value={recap.engagement_rate} testId="recap-er" />
        <Headline label="Posts published" value={recap.posts_published} testId="recap-posts" />
      </div>
      <p className="mt-2 text-xs text-neutral-500">Views {recap.views_note}. Engagement rate as reported: {recap.engagement_label}.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
        <div><p className="text-xs font-semibold text-neutral-600">Per audio</p>{recap.per_audio.map((a) => <p key={a.title}>{a.title}: <span className="font-semibold">{a.views}</span></p>)}<p className="mt-1 text-xs text-neutral-500">Fastest mover — {recap.fastest_mover}</p></div>
        <div><p className="text-xs font-semibold text-neutral-600">Split</p>{recap.split.map((s) => <p key={s.label}>{s.label}: <span className="font-semibold">{s.value}</span></p>)}{recap.bonus && <p className="mt-1 text-xs text-neutral-500" data-testid="recap-bonus">{recap.bonus}</p>}</div>
      </div>
      <ul className="mt-4 space-y-1 text-xs text-neutral-500" data-testid="recap-footnotes">{recap.footnotes.map((f) => <li key={f}>* {f}</li>)}</ul>
      <div className="mt-3"><SourceLine source={source} note={note} /></div>
    </div>
  );
}

export function SampleBlock({ sample, owner }) {
  const t = sample.totals;
  return (
    <div id="observed-sample" className="pb-card border-dashed p-6" data-testid="wrap-sample">
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Separate · not summed</p>
      <h2 className="font-display text-xl font-bold">{sample.title}</h2>
      <p className="text-sm text-neutral-500">{sample.observed}</p>
      <p className="mt-3 rounded-xl bg-[#F7EFDD] p-3 text-sm text-neutral-700" data-testid="wrap-sample-coverage">{sample.public_coverage_note}</p>
      {owner && sample.coverage_note && <p className="mt-2 rounded-xl bg-neutral-100 p-3 text-xs text-neutral-600" data-testid="wrap-sample-owner-note">Owner-only: {sample.coverage_note}</p>}
      <p className="mt-3 text-sm" data-testid="wrap-sample-totals">Sample totals ({t.reels} reels): <span className="font-semibold">{n(t.views)}</span> views · {n(t.likes)} likes · {n(t.comments)} comments · {n(t.reposts)} reposts (IG)</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="wrap-sample-table">
          <thead><tr className="text-left text-xs text-neutral-400">{["Creator", "Views", "Likes", "Comments", "Reposts (IG)", "Published", ""].map((h) => <th key={h} className="py-1 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {sample.rows.map((r, i) => (
              <tr key={r.link} className="border-t border-black/[0.05]" data-testid={`wrap-sample-row-${i}`}>
                <td className="py-1.5 pr-3">@{r.creator}{owner && r.cross_reference && <span className="ml-1.5 rounded bg-neutral-100 px-1.5 text-[10px] text-neutral-500">in tracked run · {r.code}</span>}</td>
                <td className="py-1.5 pr-3 tabular-nums">{n(r.views)}</td><td className="py-1.5 pr-3 tabular-nums">{n(r.likes)}</td>
                <td className="py-1.5 pr-3 tabular-nums">{n(r.comments)}</td><td className="py-1.5 pr-3 tabular-nums">{n(r.reposts)}</td>
                <td className="py-1.5 pr-3">{r.published}</td>
                <td className="py-1.5"><a href={r.link} target="_blank" rel="noreferrer" className="text-[#35606E]" data-testid={`wrap-sample-link-${i}`}><ExternalLink className="h-4 w-4" /></a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">Reposts (IG): {sample.reposts_note}.</p>
    </div>
  );
}
